import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../components/Footer';
import { FiPhone, FiSend } from 'react-icons/fi';
import { MdShoppingCart } from 'react-icons/md';
import { FaWhatsapp, FaBars } from 'react-icons/fa';
import { AiOutlineUser } from 'react-icons/ai';
import Search from "../components/Search";
import axios from 'axios';
import { toast } from 'react-toastify';
import { apiClient } from '../config/apiConfig';
import '../styles/ProductCard.css';

interface Variant {
  variantId: number;
  weight: number;
  unit: "kg" | "gms" | "litre" | "ml";
  price: number;
  stock: number;
}
interface Category {
  categorieId: number;
  name: string;
}

interface Product {
  productId: number;
  productName: string;
  productPrice: number;
  productQuantity: number;
  productDescription?: string;
  productImage?: string;
  variants: Variant[];
  quantity: number;
  variant: string;
  cartQuantity: number;
  categoryName?: string;
  discountPrice?: number;
  inCart?: boolean;
  cartItemsId?: number;
}

interface CartItem {
  cartItemsId: number;
  cartId: number;
  productId: number;
  productName: string;
  variantId?: number;
  variantDescription?: string;
  priceAtPurchase: number;
  quantity: number;
  productImage?: string;
}
const Shop: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get("query") || "";

  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
  const [categories, setCategories] = useState<Category[]>([{ categorieId: 0, name: "All" }]);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const productsPerPage = 8;
  const [cartId, setCartId] = useState<number | null>(
    localStorage.getItem("cartId") ? Number(localStorage.getItem("cartId")) : null
  );
  const [customerName, setCustomerName] = useState<string | null>(null);
  useEffect(() => {
    const name = localStorage.getItem("customerName");
    if (name && name.trim() !== "" && name !== "undefined") {
      setCustomerName(name);
    } else {
      setCustomerName(null);
    }
  }, []);
  // Fetch categories and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const catRes = await apiClient.get("/Categories/GetCategories");
        const catList: Category[] = [
          { categorieId: 0, name: "All" },
          ...catRes.data.map((c: any) => ({
            categorieId: c.categorieId ?? c.CategorieId ?? c.id ?? c.categoryId ?? 0,
            name: c.name ?? c.Name ?? ""
          }))
        ];
        setCategories(catList);

        const res = await fetch("https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetAllProducts");
        if (!res.ok) throw new Error("Failed to fetch products");
        const data: Product[] = await res.json();

        const initialized = data.map((p: any) => ({
          ...p,
          // normalize variants array (backend may use `Variants` or `variants`)
          variants: p.variants ?? p.Variants ?? [],
          // normalize category name coming from backend which may nest it under Category or Categories
          categoryName:
            (p.Category && (p.Category.Name ?? p.Category.name)) ||
            p.categoryName ||
            (p.Categories && (p.Categories.Name ?? p.Categories.name)) ||
            (p.Category && (p.Category.Name ?? p.Category.name)) ||
            "",
          quantity: 1,
          variant: (p.variants ?? p.Variants ?? []).length > 0 ? `${(p.variants ?? p.Variants)[0].weight}${(p.variants ?? p.Variants)[0].unit}` : "100g",
          cartQuantity: 0,
        }));

        setProducts(initialized);
      } catch (err) {
        console.error("Error fetching products:", err);
        toast.error("Failed to fetch products");
      }
    };

    fetchData();

    const storedCartId = localStorage.getItem("cartId");
    if (storedCartId) setCartId(parseInt(storedCartId));
  }, []);

  useEffect(() => {
    const fetchProductsByCategory = async () => {
      try {
        let data: Product[] = [];

        if (selectedCategoryId === 0) {
          const res = await apiClient.get("/AddProduct/GetAllProducts");
          data = res.data;
        } else {
          // Backend expects categoryId (integer) - try several URL patterns as fallback
          const encodedId = encodeURIComponent(String(selectedCategoryId));
          const candidateUrls = [
            `https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetProductsByCategory?categoryId=${encodedId}`,
            `https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetProductsByCategory/${encodedId}`,
            `https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetProductsByCategory?categoryid=${encodedId}`,
            `https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetProductsByCategory?id=${encodedId}`,
          ];

          let lastErr: any = null;
          for (const u of candidateUrls) {
            try {
              const res = await apiClient.get(u.replace('https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net', ''));
              data = res.data;
              lastErr = null;
              break;
            } catch (err: any) {
              lastErr = err;
              // try next candidate
              continue;
            }
          }

          if (lastErr) {
            // none of the candidate URLs worked - throw the last error so outer catch handles it
            throw lastErr;
          }
        }

        const initialized = data.map((p: any) => ({
          ...p,
          variants: p.variants ?? p.Variants ?? [],
          categoryName:
            (p.Category && (p.Category.Name ?? p.Category.name)) ||
            p.categoryName ||
            (p.Categories && (p.Categories.Name ?? p.Categories.name)) ||
            "",
          quantity: 1,
          variant:
            (p.variants ?? p.Variants ?? []).length > 0
              ? `${(p.variants ?? p.Variants ?? [])[0].weight}${(p.variants ?? p.Variants ?? [])[0].unit}`
              : "100g",
          cartQuantity: 0,
        }));

        // ✅ Update both products and filtered products
        setProducts(initialized);

        // ✅ Apply search filter if present in URL
        let temp = [...initialized];
        if (queryParam) {
          temp = temp.filter((p) =>
            p.productName.toLowerCase().includes(queryParam.toLowerCase())
          );
        }

        setFilteredProducts(temp);
        setCurrentPage(1);

        // ✅ Sync cart items with products after loading
        await fetchCart();
      } catch (error) {
        console.error("Error fetching category products:", error);
        toast.error("Failed to fetch products by category");
      }
    };

    fetchProductsByCategory();
  }, [selectedCategoryId, queryParam]);
  // ✅ Removed redundant filter useEffect - handled in fetchProductsByCategory above


  const handleSearch = (term: string) => {
    if (term.trim()) navigate(`/shop?query=${encodeURIComponent(term)}`);
    else navigate("/shop");
  };

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  // Helper to update a product in both products and filteredProducts lists
  const updateProductInLists = (productId: number, updater: (p: Product) => Product) => {
    setProducts(prev => prev.map(p => p.productId === productId ? updater(p) : p));
    setFilteredProducts(prev => prev.map(p => p.productId === productId ? updater(p) : p));
  };

  const handleAddToCart = async (product: Product): Promise<boolean> => {
    try {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) {
        toast.warning("Please login to continue.");
        navigate("/login");
        return false;
      }

      const selectedVariant =
        product.variants.length > 0
          ? product.variants.find(v => `${v.weight}${v.unit}` === product.variant)
          : null;

      const cartItemData = {
        customerId: Number(customerId),
        productId: product.productId,
        variantId: selectedVariant?.variantId ?? null,
        priceAtPurchase: selectedVariant?.price ?? product.productPrice,
        quantity: product.quantity || 1,
      };

      const response = await apiClient.post(
        "/Cart/CreateOrAddCartItem",
        cartItemData
      );

      if (response.status === 200) {
        const { cartId } = response.data;
        if (cartId) {
          setCartId(cartId);
          localStorage.setItem("cartId", cartId.toString());
        }

        // ✅ Refresh cart from backend to get actual cartItemsId
        await fetchCart();

        // ✅ Update product UI to show in cart
        updateProductInLists(product.productId, (p) => ({
          ...p,
          inCart: true,
          quantity: 1,
        }));

        toast.success(
          `${product.quantity} × ${product.productName} (${product.variant}) added to cart!`
        );
        return true;
      }
      return false;
    } catch (error: any) {
      console.error(
        "Error adding to cart:",
        error.response?.data || error.message
      );
      toast.error("Failed to add product to cart");
      return false;
    }
  };
  const fetchCart = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) return;

      const response = await apiClient.get(
        `/Cart/GetCartByCustomerId?customerId=${customerId}`
      );

      const items = response.data.items || [];
      setCartItems(items);

      // ✅ Sync Shop product quantity with cart
      const updateProducts = (prevProducts: Product[]) =>
        prevProducts.map((product) => {
          const cartItem = items.find(
            (c: any) => c.productId === product.productId
          );

          if (cartItem) {
            return {
              ...product,
              inCart: true,
              quantity: cartItem.quantity,
            };
          }

          return {
            ...product,
            inCart: false,
            quantity: 1,
          };
        });

      setProducts((prev) => updateProducts(prev));
      setFilteredProducts((prev) => updateProducts(prev));

    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const updateProductQuantity = async (product: Product, newQty: number) => {
    if (newQty < 1) return;

    // UI update
    updateProductInLists(product.productId, p =>
      p.productId === product.productId
        ? { ...p, quantity: newQty }
        : p
    );

    try {
      const variant = product.variants.find(v => `${v.weight}${v.unit}` === product.variant);
      const cartItem = cartItems.find(
        c => c.productId === product.productId && c.variantId === (variant?.variantId || 0)
      );

      if (!cartItem) {
        console.warn("Cart item not found for product:", product.productId);
        return;
      }

      // ✅ backend update
      await apiClient.put(
        "/Cart/UpdateQuantity",
        {
          CartItemsId: cartItem.cartItemsId,
          Quantity: newQty
        }
      );

      // local cart sync
      setCartItems(prev =>
        prev.map(c =>
          c.cartItemsId === cartItem.cartItemsId
            ? { ...c, quantity: newQty }
            : c
        )
      );

    } catch (err: any) {
      console.error("Quantity update failed:", err.response?.data || err.message);
      toast.error("Failed to update quantity");
    }
  };

  const handleIncrease = async (item: CartItem) => {
    try {
      const updatedCart = cartItems.map((i) =>
        i.cartItemsId === item.cartItemsId
          ? { ...i, quantity: i.quantity + 1 }
          : i
      );
      setCartItems(updatedCart);

      // Optional: update quantity in backend
      await apiClient.put(`/Cart/UpdateQuantity`, {
        cartItemsId: item.cartItemsId,
        quantity: item.quantity + 1,
      });
    } catch (error) {
      console.error("Error increasing quantity:", error);
    }
  };

  const handleDecrease = async (item: CartItem) => {
    if (item.quantity <= 1) return; // prevent 0 quantity
    try {
      const updatedCart = cartItems.map((i) =>
        i.cartItemsId === item.cartItemsId
          ? { ...i, quantity: i.quantity - 1 }
          : i
      );
      setCartItems(updatedCart);

      // Optional: update quantity in backend
      await apiClient.put(`/Cart/UpdateQuantity`, {
        cartItemsId: item.cartItemsId,
        quantity: item.quantity - 1,
      });
    } catch (error) {
      console.error("Error decreasing quantity:", error);
    }
  };
  const totalCount = cartItems.length;
  return (
    <div className="goto-here">
      {/* Top Info */}
      <div className="py-1 bg-primary w-100">
        <div className="container">
          <div className="row d-flex align-items-center text-white">
            <div className="col-12 col-md d-flex align-items-center mb-1 mb-md-0">
              <FiPhone size={14} className="mr-2" />
              <span>+91 9148761750</span>
            </div>

            <div className="col-12 col-md d-flex align-items-center mb-1 mb-md-0">
              <FiSend size={14} className="mr-2" />
              <span>care@fhome.shop</span>
            </div>

            <div className="col-12 col-md text-md-right">
              <span>3-5 Business days delivery & Free Returns</span>
            </div>
          </div>
        </div>
      </div>
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark ftco_navbar bg-dark ftco-navbar-light" id="ftco-navbar">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img src="/images/fhome-logo.jpg" alt="" style={{ height: "55px", marginRight: "8px", borderRadius: "50%", width: "80px" }} />
          </a>
          <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#ftco-nav">
            <FaBars className="mr-2" /> Menu
          </button>

          <div className="collapse navbar-collapse" id="ftco-nav">
            <ul className="navbar-nav ml-auto align-items-center">

              <li className="nav-item active"><a href="/" className="nav-link">Home</a></li>

              <li className="nav-item dropdown">
                <a className="nav-link dropdown-toggle" href="#" id="dropdown04" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Shop</a>
                <div className="dropdown-menu" aria-labelledby="dropdown04">
                  <a className="dropdown-item" href="/shop">Shop</a>
                </div>
              </li>

              <li className="nav-item"><a href="/about" className="nav-link">About</a></li>
              <li className="nav-item"><a href="/contact" className="nav-link">Contact</a></li>

              {/* 🔥 Search as a list item - neatly aligned */}
              <li className="nav-item mx-2" style={{ width: "200px" }}>
                <Search onSearchResults={handleSearch} />
              </li>

              <li className="nav-item cta cta-colored"> <a href="/cart" className="nav-link">
                <span><MdShoppingCart size={18} /></span> [{totalCount}]
              </a></li>

              <li className="nav-item">
                <a
                  href="/my-account"
                  className="nav-link d-flex align-items-center"
                  style={{ gap: "6px" }}
                >
                  <AiOutlineUser size={24} style={{ color: "#898989" }} />

                  {customerName ? (
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#999",
                        maxWidth: "120px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={`Hi, ${customerName}`}
                    >
                      Hi, {customerName}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#999"
                      }}
                    >
                      Login
                    </span>
                  )}
                </a>
              </li>
            </ul>
          </div>

        </div>
      </nav>

      {/* Category Dropdown */}
      <section className="ftco-section">
        <div className="row justify-content-center">
          <div className="col-md-6 mb-4 text-center">
            <label htmlFor="categoryDropdown" className="form-label">Select Category:</label>
            <select
              id="categoryDropdown"
              className="form-select"
              value={selectedCategoryId}
              onChange={e => setSelectedCategoryId(Number(e.target.value))}
            >
              {categories.map(cat => (
                <option key={cat.categorieId} value={cat.categorieId}>{cat.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Product Grid */}
        <div className="row g-2 g-md-3">
          {paginatedProducts.length > 0 ? (
            paginatedProducts.map((product) => (
              <div
                key={product.productId}
                className="col-6 col-sm-4 col-md-3 col-lg-2-4 mb-3"
              >
                <div className="card product-card text-center">
                  {/* IMAGE */}
                  <img
                    src={product.productImage ?? "/images/default-product.jpg"}
                    className="card-img-top"
                    alt={product.productName}
                    loading="lazy"
                    onClick={() =>
                      navigate(`/product-single/${product.productId}`)
                    }
                  />

                  <div className="card-body">
                    {/* NAME */}
                    <h6
                      className="product-title"
                      onClick={() =>
                        navigate(`/product-single/${product.productId}`)
                      }
                    >
                      {product.productName}
                    </h6>

                    {/* VARIANT */}
                    <select
                      className="form-control mb-2"
                      value={product.variant}
                      onChange={(e) =>
                        updateProductInLists(product.productId, (p) => ({
                          ...p,
                          variant: e.target.value,
                        }))
                      }
                    >
                      {product.variants.map((v) => (
                        <option
                          key={v.variantId}
                          value={`${v.weight}${v.unit}`}
                        >
                          {v.weight}
                          {v.unit} - ₹{v.price}
                        </option>
                      ))}
                    </select>

                    {/* PRICE */}
                    <p className="price mb-2">
                      ₹
                      {
                        product.variants.find(
                          (v) =>
                            `${v.weight}${v.unit}` === product.variant
                        )?.price
                      }
                    </p>

                    {/* CART ACTION */}
                    {!product.inCart ? (
                      <button
                        className="btn btn-success w-100 add-cart-btn"
                        onClick={() => {
                          updateProductInLists(product.productId, (p) => ({
                            ...p,
                            inCart: true,
                            quantity: 1,
                          }));
                          handleAddToCart(product);
                        }}
                      >
                        🛒 Add to Cart
                      </button>
                    ) : (
                      <div className="qty-box">
                        <button
                          className="qty-btn"
                          onClick={() =>
                            updateProductQuantity(
                              product,
                              product.quantity - 1
                            )
                          }
                        >
                          −
                        </button>
                        <span className="qty-count">
                          {product.quantity}
                        </span>

                        <button
                          className="qty-btn"
                          onClick={() =>
                            updateProductQuantity(
                              product,
                              product.quantity + 1
                            )
                          }
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p>No products found</p>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="row mt-5">
            <div className="col text-center">
              <div className="block-27">
                <ul>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); setCurrentPage(prev => Math.max(prev - 1, 1)); }} style={{ pointerEvents: currentPage === 1 ? "none" : "auto", opacity: currentPage === 1 ? 0.5 : 1 }}>&lt;</a>
                  </li>
                  {[...Array(totalPages)].map((_, idx) => (
                    <li key={idx} className={currentPage === idx + 1 ? "active" : ""}>
                      <a href="#" onClick={(e) => { e.preventDefault(); setCurrentPage(idx + 1); }}>{idx + 1}</a>
                    </li>
                  ))}
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); setCurrentPage(prev => Math.min(prev + 1, totalPages)); }} style={{ pointerEvents: currentPage === totalPages ? "none" : "auto", opacity: currentPage === totalPages ? 0.5 : 1 }}>&gt;</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </section>

      <Footer />

      {/* WhatsApp Floating */}
      <a href="https://wa.me/919148761750" className="whatsapp-float" target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp">
        <FaWhatsapp size={40} color="#25D366" />
      </a>
    </div>
  );
};

export default Shop;

